// ─────────────────────────────────────────────────────────────────
// Glass-i — Supplier Payables LIST page (Stage 6P.7).
//
// Server-paginated read of "what does each supplier still owe us"
// at /erp/supplier-payments/payables.
//
// Columns:
//   Supplier (+ GSTIN tag) · Bill No · Supplier Invoice No
//   Invoice Date · Payable Amount · Paid Amount · Balance Amount
//   Payment Status (UNPAID / PARTIALLY_PAID / PAID / OVERPAID)
//   Days Outstanding · Actions (⋯)
//
// Toolbar:
//   Search (q over supplier_name / supplier_gstin /
//           supplier_invoice_no / purchase_bill_no / posting_no)
//   Payment status filter · Posting date range · Clear · Refresh
//
// Per-row actions:
//   • Create Payment → /erp/supplier-payments/new?bill_id=…
//                       &supplier_id=…&posting_id=…
//   • View Bill      → /erp/purchase/bills/:purchase_bill_id
//   • View History   → /erp/supplier-payments/history/supplier/:supplier_id
//
// Backend remains the authority for ALL payable math:
//   payable = pap.grand_total (POSTED posting)
//   paid    = Σ ACTIVE allocations whose PARENT supplier_payment is POSTED
//   balance = payable - paid
//   payment_status derived via BALANCE_EPSILON
//   days_outstanding derived from supplier_invoice_date (or posting_date)
// This page is a pure read + dispatch surface — never recomputes.
//
// Style:
//   Mirrors PurchaseAccountingPostingsPage — slate-50 header strip,
//   22 px row density, sticky toolbar, ⋯ row-actions menu, reuses the
//   shared GoodsReceiptsPage.css via the .grn-* wrapper so no new CSS
//   file is added.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, DatePicker, Dropdown, Input, Select,
  Space, Spin, Table, Tag, Tooltip, message
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, MoreOutlined,
  EyeOutlined, DollarOutlined, HistoryOutlined, WalletOutlined,
  FileTextOutlined
} from '@ant-design/icons';

import { supplierPaymentApi } from './supplierPaymentApi';
import { PAYABLE_BALANCE_STATUSES } from './supplierPaymentHelpers';
import useAuth from '../../../hooks/useAuth';
import '../purchase/GoodsReceiptsPage.css';

const { RangePicker } = DatePicker;


// ─────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS  = ['25', '50', '100'];
const DATE_FMT           = 'DD-MMM-YYYY';
const ISO_FMT            = 'YYYY-MM-DD';
const SEARCH_DEBOUNCE_MS = 250;

const PAYMENT_STATUS_LABELS = {
  UNPAID:         'Unpaid',
  PARTIALLY_PAID: 'Partially Paid',
  PAID:           'Paid',
  OVERPAID:       'Overpaid'
};

const PAYMENT_STATUS_COLORS = {
  UNPAID:         'error',
  PARTIALLY_PAID: 'gold',
  PAID:           'success',
  OVERPAID:       'magenta'
};

const STATUS_OPTIONS = PAYABLE_BALANCE_STATUSES.map((v) => ({
  value: v,
  label: PAYMENT_STATUS_LABELS[v] || v
}));


// ─────────────────────────────────────────────────────────────────
// Pure helpers — kept local so the page doesn't depend on a shared
// formatter module that could drift.
// ─────────────────────────────────────────────────────────────────

function fmtDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const day   = String(d.getDate()).padStart(2, '0');
  const month = d.toLocaleString('en-US', { month: 'short' });
  const year  = d.getFullYear();
  return `${day}-${month}-${year}`;
}

// ₹ grouping is locale-aware (en-IN) so 12,34,567.89 is rendered the
// same way as elsewhere on the control plane. Falsy / non-finite
// inputs collapse to an em-dash so empty rows don't read as "₹0".
function fmtMoney(value) {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return '—';
  try {
    return n.toLocaleString('en-IN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  } catch {
    return n.toFixed(2);
  }
}

function pickField(row, snake, camel) {
  if (!row) return undefined;
  if (camel in row && row[camel] !== undefined && row[camel] !== null) return row[camel];
  if (snake in row && row[snake] !== undefined && row[snake] !== null) return row[snake];
  return undefined;
}

function errMessage(err, fallback) {
  return err?.response?.data?.error?.message || err?.message || fallback;
}

function paymentStatusLabel(status) {
  if (!status) return '';
  const key = String(status).trim().toUpperCase();
  return PAYMENT_STATUS_LABELS[key] || key.replace(/_/g, ' ');
}

function paymentStatusColor(status) {
  if (!status) return 'default';
  const key = String(status).trim().toUpperCase();
  return PAYMENT_STATUS_COLORS[key] || 'default';
}


// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────

export default function SupplierPayablesPage() {
  const navigate = useNavigate();
  const { me } = useAuth();

  // Wildcard '*' is the master role; explicit
  // `erp.supplier_payments.*` strings are the Stage 6P.7 perms.
  const hasPerm = useMemo(() => {
    const list = Array.isArray(me?.permissions) ? me.permissions : [];
    const set  = new Set(list);
    const wildcard = set.has('*');
    return (key) => wildcard || set.has(key);
  }, [me]);

  const canViewReports  = hasPerm('erp.supplier_payments.reports.view');
  const canCreate       = hasPerm('erp.supplier_payments.create');

  // ── Toolbar state ────────────────────────────────────────────
  const [searchInput, setSearchInput] = useState('');
  const [search,      setSearch]      = useState('');
  const [status,      setStatus]      = useState(null);
  const [dateRange,   setDateRange]   = useState(null);

  // ── Pagination state (client-side over the API limit/offset) ─
  // The balance service exposes a hard cap of 500 per call — we
  // request the current page window only and let it tell us if a
  // next page exists by overflowing the cap.
  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [total,    setTotal]    = useState(0);

  // ── Data state ───────────────────────────────────────────────
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(false);

  // ── Debounced search wiring ──────────────────────────────────
  // Keep the visible input snappy, but only re-fetch once the
  // operator pauses typing — same pattern as the postings list.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch((prev) => {
        if (prev === searchInput) return prev;
        setPage(1);
        return searchInput;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchInput]);

  // ── Load ─────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!canViewReports) return;
    setLoading(true);
    try {
      const limit  = pageSize;
      const offset = (page - 1) * pageSize;
      // Probe-fetch one extra row to detect "has more" — the
      // /payables endpoint doesn't return a total count, so we
      // synthesise it from the requested window.
      const params = {
        limit:  limit + 1,
        offset
      };
      const needle = (search || '').trim();
      if (needle) {
        params.q      = needle;
        params.search = needle;
      }
      if (status) params.payment_status = status;
      if (Array.isArray(dateRange) && dateRange[0] && dateRange[1]) {
        params.from_date = dateRange[0].format
          ? dateRange[0].format(ISO_FMT)
          : new Date(dateRange[0]).toISOString().slice(0, 10);
        params.to_date   = dateRange[1].format
          ? dateRange[1].format(ISO_FMT)
          : new Date(dateRange[1]).toISOString().slice(0, 10);
      }

      const out = await supplierPaymentApi.listPayables(params);
      const list = Array.isArray(out) ? out
        : Array.isArray(out?.rows)  ? out.rows
        : Array.isArray(out?.data)  ? out.data
        : Array.isArray(out?.items) ? out.items
        : [];

      // Slice off the probe row and decide whether a next page
      // exists; the running total advances by the page window so
      // AntD's pager shows a sensible boundary.
      const hasMore = list.length > limit;
      const windowRows = hasMore ? list.slice(0, limit) : list;
      const seenSoFar  = offset + windowRows.length;
      const projected  = hasMore ? seenSoFar + 1 : seenSoFar;

      setRows(windowRows);
      setTotal(projected);
    } catch (e) {
      message.error({
        content: errMessage(e, 'Failed to load supplier payables.'),
        key: 'sp-payables-list'
      });
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [canViewReports, page, pageSize, search, status, dateRange]);

  useEffect(() => { load(); }, [load]);

  // ── Toolbar handlers — any filter change resets page to 1 ────
  const onStatus = (v) => { setStatus(v || null);    setPage(1); };
  const onDates  = (v) => { setDateRange(v || null); setPage(1); };
  const onReset  = () => {
    setSearchInput('');
    setSearch('');
    setStatus(null);
    setDateRange(null);
    setPage(1);
  };

  // ── Row navigation ───────────────────────────────────────────
  const openCreatePayment = useCallback((row) => {
    const billId     = pickField(row, 'purchase_bill_id', 'purchaseBillId');
    const postingId  = pickField(row, 'purchase_accounting_posting_id', 'purchaseAccountingPostingId');
    const supplierId = pickField(row, 'supplier_id', 'supplierId');
    const params = new URLSearchParams();
    if (supplierId) params.set('supplier_id', supplierId);
    if (billId)     params.set('bill_id', billId);
    if (postingId)  params.set('posting_id', postingId);
    const qs = params.toString();
    navigate(`/erp/supplier-payments/new${qs ? `?${qs}` : ''}`);
  }, [navigate]);

  const openBill = useCallback((row) => {
    const billId = pickField(row, 'purchase_bill_id', 'purchaseBillId');
    if (!billId) {
      message.info('This payable is not linked to a purchase bill.');
      return;
    }
    navigate(`/erp/purchase/bills/${encodeURIComponent(billId)}`);
  }, [navigate]);

  const openHistory = useCallback((row) => {
    const supplierId = pickField(row, 'supplier_id', 'supplierId');
    if (!supplierId) {
      message.info('Supplier reference unavailable for this row.');
      return;
    }
    navigate(`/erp/supplier-payments/history/supplier/${encodeURIComponent(supplierId)}`);
  }, [navigate]);

  // ── Row-action menu builder ──────────────────────────────────
  const buildRowMenu = useCallback((row) => {
    const items = [];
    if (canCreate) {
      const billId = pickField(row, 'purchase_bill_id', 'purchaseBillId');
      items.push({
        key:   'create-payment',
        icon:  <DollarOutlined />,
        label: 'Create Payment',
        // No bill / posting reference is a data anomaly — disable
        // the CTA rather than dispatching a broken navigation.
        disabled: !billId
      });
    }
    items.push({
      key:   'view-bill',
      icon:  <FileTextOutlined />,
      label: 'View Bill'
    });
    items.push({
      key:   'view-history',
      icon:  <HistoryOutlined />,
      label: 'View History'
    });
    return items;
  }, [canCreate]);

  const onRowMenu = useCallback((key, row) => {
    switch (key) {
      case 'create-payment': return openCreatePayment(row);
      case 'view-bill':      return openBill(row);
      case 'view-history':   return openHistory(row);
      default:               return undefined;
    }
  }, [openCreatePayment, openBill, openHistory]);

  // ── Columns ──────────────────────────────────────────────────
  const columns = useMemo(() => ([
    {
      title:     'Supplier',
      dataIndex: 'supplierName',
      render: (_v, row) => {
        const name  = pickField(row, 'supplier_name',  'supplierName')  || '—';
        const gstin = pickField(row, 'supplier_gstin', 'supplierGstin');
        return (
          <div className="grn-supplier">
            <Tooltip title={name}>
              <span className="grn-supplier__name">{name}</span>
            </Tooltip>
            {gstin
              ? <Tag color="blue" className="grn-gstin-tag">{gstin}</Tag>
              : null}
          </div>
        );
      }
    },
    {
      title:     'Bill No',
      dataIndex: 'purchaseBillNo',
      width:     160,
      render: (_v, row) => {
        const billNo = pickField(row, 'purchase_bill_no', 'purchaseBillNo');
        if (!billNo) return <span className="grn-muted">—</span>;
        return (
          <button
            type="button"
            className="grn-link"
            onClick={() => openBill(row)}
          >
            {billNo}
          </button>
        );
      }
    },
    {
      title:     'Supplier Invoice No',
      dataIndex: 'supplierInvoiceNo',
      width:     170,
      render: (_v, row) => {
        const ref = pickField(row, 'supplier_invoice_no', 'supplierInvoiceNo');
        if (!ref) return <span className="grn-muted">—</span>;
        return <span className="grn-mono">{ref}</span>;
      }
    },
    {
      title:     'Invoice Date',
      dataIndex: 'supplierInvoiceDate',
      width:     130,
      render: (_v, row) => (
        <span className="grn-mono">
          {fmtDate(pickField(row, 'supplier_invoice_date', 'supplierInvoiceDate'))}
        </span>
      )
    },
    {
      title:     'Payable Amount',
      dataIndex: 'payableAmount',
      width:     140,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(pickField(row, 'payable_amount', 'payableAmount'))}
        </span>
      )
    },
    {
      title:     'Paid Amount',
      dataIndex: 'paidAmount',
      width:     130,
      align:     'right',
      render: (_v, row) => (
        <span className="grn-mono grn-line-count">
          {fmtMoney(pickField(row, 'paid_amount', 'paidAmount'))}
        </span>
      )
    },
    {
      title:     'Balance Amount',
      dataIndex: 'balanceAmount',
      width:     140,
      align:     'right',
      render: (_v, row) => {
        // Prefer the server-rounded value; fall back to a derived
        // computation only when the backend omits it (defence-in-
        // depth — the service always emits it today).
        let bal = pickField(row, 'balance_amount', 'balanceAmount');
        if (bal === undefined || bal === null || bal === '') {
          const p = Number(pickField(row, 'payable_amount', 'payableAmount') || 0);
          const d = Number(pickField(row, 'paid_amount',    'paidAmount')    || 0);
          bal = Number.isFinite(p) && Number.isFinite(d) ? (p - d) : null;
        }
        return (
          <span className="grn-mono grn-line-count">
            <strong>{fmtMoney(bal)}</strong>
          </span>
        );
      }
    },
    {
      title:     'Payment Status',
      dataIndex: 'paymentStatus',
      width:     150,
      render: (_v, row) => {
        const v = pickField(row, 'payment_status', 'paymentStatus');
        if (!v) return <Tag className="grn-status-tag" color="default">—</Tag>;
        return (
          <Tag color={paymentStatusColor(v)} className="grn-status-tag">
            {paymentStatusLabel(v)}
          </Tag>
        );
      }
    },
    {
      title:     'Days Outstanding',
      dataIndex: 'daysOutstanding',
      width:     140,
      align:     'right',
      render: (_v, row) => {
        const d = pickField(row, 'days_outstanding', 'daysOutstanding');
        if (d === undefined || d === null) return <span className="grn-muted">—</span>;
        const n = Number(d);
        if (!Number.isFinite(n)) return <span className="grn-muted">—</span>;
        return (
          <span className="grn-mono grn-line-count">
            {n}
          </span>
        );
      }
    },
    {
      title:     '',
      key:       '_actions',
      width:     56,
      fixed:     'right',
      align:     'center',
      render: (_v, row) => {
        const items = buildRowMenu(row);
        return (
          <Dropdown
            menu={{
              items,
              onClick: ({ key, domEvent }) => {
                if (domEvent && domEvent.stopPropagation) domEvent.stopPropagation();
                onRowMenu(key, row);
              }
            }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className="grn-actions-btn"
              icon={<MoreOutlined />}
              onClick={(e) => e.stopPropagation()}
              aria-label="Row actions"
            />
          </Dropdown>
        );
      }
    }
  ]), [openBill, buildRowMenu, onRowMenu]);

  // ── Render ───────────────────────────────────────────────────
  if (!canViewReports) {
    return (
      <div className="grn-shell">
        <Alert
          type="warning"
          showIcon
          message="You don't have permission to view supplier payables."
          description="Ask your master account to grant erp.supplier_payments.reports.view."
        />
      </div>
    );
  }

  return (
    <div className="grn-shell">
      {/* Header strip */}
      <div className="grn-headstrip">
        <div className="grn-headstrip__lead">
          <span className="grn-headstrip__icon"><WalletOutlined /></span>
          <div className="grn-headstrip__text">
            <span className="grn-headstrip__title">Supplier Payables</span>
            <span className="grn-headstrip__subtitle">
              Outstanding bill-level balances over POSTED purchase
              accounting postings. Backend owns payable / paid /
              balance derivation and payment-status classification —
              this surface reads and dispatches Create Payment, View
              Bill and View History only.
            </span>
          </div>
        </div>
        <Space size={6}>
          <Tag color="blue" className="grn-status-tag">
            <strong>{total}</strong>{total === 1 ? ' bill' : ' bills'}
          </Tag>
        </Space>
      </div>

      {/* Sticky toolbar */}
      <div className="grn-toolbar">
        <Input
          allowClear
          size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search supplier, GSTIN, invoice no, bill no…"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onPressEnter={(e) => {
            // Flush debounce on Enter.
            setSearchInput(e.target.value);
            setSearch(e.target.value);
            setPage(1);
          }}
          style={{ width: 320 }}
        />
        <Select
          allowClear
          size="small"
          placeholder="All payment statuses"
          value={status || undefined}
          onChange={onStatus}
          options={STATUS_OPTIONS}
          style={{ width: 200 }}
        />
        <RangePicker
          size="small"
          value={dateRange}
          onChange={onDates}
          format={DATE_FMT}
          allowClear
          placeholder={['From', 'To']}
          style={{ width: 240 }}
        />
        <Tooltip title="Clear filters">
          <Button size="small" onClick={onReset}>Clear</Button>
        </Tooltip>
        <div className="grn-toolbar__spacer" />
        <Tooltip title="Refresh">
          <Button
            size="small"
            icon={<ReloadOutlined />}
            onClick={load}
          />
        </Tooltip>
      </div>

      {/* Grid card */}
      <Card className="grn-card" bordered={false} bodyStyle={{ padding: 0 }}>
        <Spin spinning={loading}>
          <Table
            className="grn-grid"
            size="small"
            rowKey={(r) =>
              pickField(r, 'purchase_accounting_posting_id', 'purchaseAccountingPostingId')
              || pickField(r, 'purchase_bill_id', 'purchaseBillId')
              || pickField(r, 'purchase_bill_no', 'purchaseBillNo')}
            dataSource={rows}
            columns={columns}
            sticky
            scroll={{ x: 'max-content' }}
            onRow={(row) => ({
              onDoubleClick: () => {
                if (canCreate) openCreatePayment(row);
                else openBill(row);
              }
            })}
            pagination={{
              current:           page,
              pageSize,
              total,
              showSizeChanger:   true,
              pageSizeOptions:   PAGE_SIZE_OPTIONS,
              showQuickJumper:   total > pageSize,
              size:              'small',
              showTotal: (count, range) =>
                count === 0
                  ? 'No rows'
                  : `${range[0]}–${range[1]} of ${count}`,
              onChange: (nextPage, nextSize) => {
                if (nextSize !== pageSize) {
                  setPageSize(nextSize);
                  setPage(1);
                } else {
                  setPage(nextPage);
                }
              }
            }}
            locale={{
              emptyText: loading
                ? ' '
                : 'No outstanding supplier payables.'
            }}
          />
        </Spin>
      </Card>
    </div>
  );
}

// `EyeOutlined` kept in the import block so future row actions that
// add a read-only inline drawer can reuse it without touching the
// import header.
void EyeOutlined;
